import {
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { SidebarRail } from "@volca/ui/components/ui/sidebar";
import { cn } from "@volca/ui/lib/utils";

const railTension = (overshootPx: number) => {
  if (overshootPx <= 0) return { stretch: 0, growY: 1 };
  const pull = 1 - Math.exp(-overshootPx / 90);
  return {
    stretch: Math.round(32 * pull),
    growY: 1 + 1.1 * pull,
  };
};

const CLICK_DELAY_MS = 250;
const COLLAPSE_PULL_PX = 48;

const setTension = (target: HTMLButtonElement, overshootPx: number) => {
  const tension = railTension(overshootPx);
  target.style.setProperty("--rail-stretch", `${tension.stretch}px`);
  target.style.setProperty("--rail-grow-y", String(tension.growY));
};

const clamp = (width: number, min: number, max: number) =>
  Math.min(max, Math.max(min, Math.round(width)));

export const NavPanelResize = ({
  width,
  min,
  max,
  defaultWidth,
  collapsed,
  onWidth,
  onCommit,
  onCollapse,
  onExpand,
  onLiveWidth,
  onResizing,
}: {
  width: number;
  min: number;
  max: number;
  defaultWidth: number;
  collapsed: boolean;
  onWidth: (width: number) => void;
  onCommit: () => void;
  onCollapse: () => void;
  onExpand: () => void;
  onLiveWidth: (width: number | null) => void;
  onResizing: (resizing: boolean) => void;
}) => {
  const drag = useRef<{ x: number; width: number; raw: number } | null>(null);
  const dragged = useRef(false);
  const clickTimer = useRef<number | null>(null);

  const resizeTo = (next: number) => {
    onResizing(true);
    onWidth(clamp(next, min, max));
    onCommit();
    if (collapsed) onExpand();
    onLiveWidth(null);
    requestAnimationFrame(() => onResizing(false));
  };

  const endDrag = (target: HTMLButtonElement) => {
    // Collapse fires this mid-drag. Pointer-up then runs again on the closed
    // column, and that second pass was treating the release as an open.
    if (!drag.current) return;
    const raw = drag.current.raw;
    delete target.dataset.resizing;
    requestAnimationFrame(() => setTension(target, 0));
    drag.current = null;
    if (collapsed) {
      onLiveWidth(null);
      onResizing(false);
      if (!dragged.current) return;
      if (raw >= min) {
        onWidth(clamp(raw, min, max));
        onCommit();
      }
      onExpand();
      return;
    }
    onResizing(false);
    if (dragged.current) onCommit();
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (collapsed) {
      if (event.key !== "ArrowRight") return;
      onExpand();
      event.preventDefault();
      return;
    }
    const step = event.shiftKey ? 24 : 8;
    if (event.key === "ArrowLeft") resizeTo(width - step);
    else if (event.key === "ArrowRight") resizeTo(width + step);
    else if (event.key === "Home") resizeTo(min);
    else if (event.key === "End") resizeTo(max);
    else return;
    event.preventDefault();
  };

  return (
    <SidebarRail
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={collapsed ? 0 : width}
      aria-valuemin={collapsed ? 0 : min}
      aria-valuemax={max}
      aria-label={collapsed ? "Open navigation" : "Resize navigation"}
      title={
        collapsed
          ? "Drag right or click to open."
          : "Drag to resize. Drag left or click to collapse. Double-click to reset width."
      }
      tabIndex={0}
      className={cn({ "cursor-e-resize": collapsed })}
      onPointerDown={(event: ReactPointerEvent<HTMLButtonElement>) => {
        if (event.button !== 0) return;
        dragged.current = false;
        const start = collapsed ? 0 : width;
        drag.current = { x: event.clientX, width: start, raw: start };
        onResizing(true);
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event: ReactPointerEvent<HTMLButtonElement>) => {
        if (!drag.current) return;
        const dx = event.clientX - drag.current.x;
        if (!dragged.current && Math.abs(dx) < 4) return;
        dragged.current = true;
        event.currentTarget.dataset.resizing = "true";
        const raw = drag.current.width + dx;
        drag.current.raw = raw;
        const target = event.currentTarget;
        if (collapsed) {
          onLiveWidth(Math.max(0, Math.min(max, raw)));
          return;
        }
        setTension(target, Math.max(0, raw - max, min - raw));
        if (raw < min - COLLAPSE_PULL_PX) {
          onCollapse();
          endDrag(target);
          return;
        }
        onWidth(clamp(raw, min, max));
      }}
      onPointerUp={(event: ReactPointerEvent<HTMLButtonElement>) =>
        endDrag(event.currentTarget)
      }
      onPointerCancel={(event: ReactPointerEvent<HTMLButtonElement>) =>
        endDrag(event.currentTarget)
      }
      onClick={() => {
        if (dragged.current) {
          dragged.current = false;
          return;
        }
        if (collapsed) {
          onExpand();
          return;
        }
        if (clickTimer.current != null) {
          window.clearTimeout(clickTimer.current);
          clickTimer.current = null;
          return;
        }
        clickTimer.current = window.setTimeout(() => {
          clickTimer.current = null;
          onCollapse();
        }, CLICK_DELAY_MS);
      }}
      onDoubleClick={() => {
        if (clickTimer.current != null) {
          window.clearTimeout(clickTimer.current);
          clickTimer.current = null;
        }
        dragged.current = false;
        resizeTo(defaultWidth);
      }}
      onKeyDown={onKeyDown}
    />
  );
};
